import { DatePipe } from '@angular/common';
import { Component, inject, input, OnInit, signal, output, computed } from '@angular/core';
import { Action } from '@shared/interfaces/actions';
import { Contact } from '@shared/models/iContact/contact';
import { UserInfo } from '@shared/models/user-info';
import { ScheduleUtil } from '@shared/utils';
import { isBefore } from 'date-fns';
import { FormOptions } from 'src/app/common/models/form-options';

@Component({
  selector: 'idel-action-card',
  templateUrl: './action-card.component.html',
  styleUrl: './action-card.component.scss',
})
export class ActionCardComponent implements OnInit {
  private readonly formOptions = inject(FormOptions);
  private readonly datePipe = inject(DatePipe);

  public readonly action = input.required<Action>();
  public readonly currentUser = input.required<UserInfo>();
  public readonly contacts = input.required<Contact[]>();
  public readonly lightMode = input<boolean>(false);

  public readonly onProcess = output<Action>();
  public readonly naviguateToMeeting = output<number>();
  public readonly naviguateToPoint = output<number>();

  public readonly recipientName = signal<string>('Inconnu');
  public readonly demanderName = signal<string>('Inconnu');
  public readonly forMySelf = signal<boolean>(false);

  public readonly isDeadlineOverdue = computed<boolean>(() => {
    const action = this.action();
    return action?.deadline ? isBefore(action.deadline, new Date()) && !this.action().done : false;
  });

  public readonly deadlineDetail = computed<string>(() => {
    const actionToProcess = this.action();

    if (!actionToProcess || !actionToProcess.deadline) return '';

    const parsedDate = this.datePipe.transform(actionToProcess.deadline, 'EEEE dd/MM/yyyy');
    const delaySinceToday = ScheduleUtil.delaySinceToday(actionToProcess.deadline);

    return `Devait être traitée pour le ${parsedDate} (${delaySinceToday})`;
  });

  ngOnInit(): void {
    this.setDemanderAndRecipientNames();
  }

  private setDemanderAndRecipientNames(): void {
    if (this.action().demander.id === null) {
      this.demanderName.set('Système');
    } else {
      const foundUser = this.formOptions.users().find((user) => user.userId === this.action().demander.id);

      if (foundUser) {
        this.demanderName.set(`${foundUser.nom.toUpperCase()} ${foundUser.prenom}`);
      } else {
        this.demanderName.set('Inconnu');
      }
    }

    if (this.action().recipient.kind === 'user') {
      const foundRecipient = this.contacts().find((contact) => contact.id === this.action().recipient.id);

      if (foundRecipient) {
        this.recipientName.set(`${foundRecipient.nom!.toUpperCase()} ${foundRecipient.prenom}`);
      }

      if (
        this.action().demander.id === this.currentUser().id &&
        this.action().recipient.id === this.currentUser().contactId
      ) {
        this.forMySelf.set(true);
      }
    } else if (this.action().recipient.kind === 'group') {
      const foundGroup = this.formOptions.groups().find((group) => group.id === this.action().groupId);
      this.recipientName.set(foundGroup ? foundGroup.description : 'Inconnu');
    } else if (this.action().recipient.kind === 'service') {
      const foundService = this.formOptions.services().find((service) => service.id === this.action().serviceId);
      this.recipientName.set(foundService ? foundService.description : 'Inconnu');
    }
  }

  public goToMeeting(meetingId: number): void {
    this.naviguateToMeeting.emit(meetingId);
  }

  public goToPoint(pointId: number): void {
    this.naviguateToPoint.emit(pointId);
  }
}
